/* eslint-disable max-len */
import React from 'react';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@components/Base/Checkbox';
import { CustomColumnDef } from '@components/Base/Table/types';
import TextDescription from '@components/Base/TextDescription';
import User from '@components/Base/UserDetails';
import { convertToTitleCase } from '@components/ProctorNgSessions/SessionTable/helper';
import { Voucher } from '@containers/vouchers/types';
import gripHorizontalIcon from '@images/grip-horizontal.svg';
import { classNames } from '@utils/classNames';
import { ValidVoucher, formatDate } from '@utils/dateFormat';

import messages from '../messages';

const EDIT_ALT = 'edit';
const DASH = '-';

export const createColumns = (): CustomColumnDef<Voucher>[] => [
  {
    id: 'voucher code',
    header: () => <FormattedMessage {...messages.voucher_code} />,
    accessorKey: 'code',
    enableSorting: false,
    hidden: false,
    cell: ({ row }) => <div>{row.original.code || DASH}</div>,
    size: 200,
    className:
      'text-left text-sm font-medium font-roboto text-gray-900 left-[0px] sticky attendee-column shadow-[inset_-1px_0px_0px_0px] shadow-gray-400/20',
    cellClassName:
      'text-left sticky left-[0px] attendee-column shadow-[inset_-1px_0px_0px_0px] z-[1] shadow-gray-400/20',
  },
  {
    id: 'Discount',
    header: () => <FormattedMessage {...messages.discount} />,
    accessorKey: 'discount_percentage',
    enableSorting: false,
    cell: ({ row }) => (row.original.discount_percentage ? `${row.original.discount_percentage}%` : '-'),
    size: 100,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'description',
    header: () => <FormattedMessage {...messages.description} />,
    accessorKey: 'description',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.description || DASH}</div>,
    size: 250,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
  },
  {
    id: 'SKU',
    header: () => <FormattedMessage {...messages.sku} />,
    accessorKey: 'sku',
    enableSorting: false,
    size: 250,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left fit-content',
    hidden: false,
    cell: ({ row }) => {
      const skuCoupons = row?.original?.sku_coupons;

      let combinedNames = '-';
      let combinedExternalIds = '';

      if (skuCoupons?.length) {
        combinedNames = skuCoupons
          .map((coupon) => coupon?.sku?.name)
          .filter(Boolean)
          .join(', ');
        combinedExternalIds = skuCoupons
          .map((coupon) => coupon?.sku?.external_id)
          .filter(Boolean)
          .join(', ');
      }

      return (
        <TextDescription
          text={combinedNames}
          showToolTip={true}
          maxNumberOfLines={2}
          textClass="w-[200px]"
          description={combinedExternalIds ?? ''}
        />
      );
    },
  },
  {
    id: 'Tags',
    header: () => <FormattedMessage {...messages.tags} />,
    accessorKey: 'tags',
    enableSorting: false,
    cell: ({ row }) => {
      const tagCoupons = row?.original?.tag_coupons;
      let combinedNames;
      if (tagCoupons?.length) {
        combinedNames = tagCoupons.map((coupon) => coupon?.tag?.value).join(', ');
      } else {
        combinedNames = '-';
      }

      if (combinedNames != '-') {
        return (
          <TextDescription text={combinedNames ?? '-'} showToolTip={true} maxNumberOfLines={2} textClass="w-[200px]" />
        );
      } else {
        return <span>{DASH}</span>;
      }
    },
    size: 220,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Max Use',
    header: () => <FormattedMessage {...messages.max_use} />,
    accessorKey: 'max_count',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.max_count || DASH}</div>,
    size: 80,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Expires On',
    header: () => <FormattedMessage {...messages.expires_on} />,
    accessorKey: 'valid_till',
    enableSorting: false,
    cell: ({ row }) => (row.original.valid_till ? ValidVoucher(new Date(row.original.valid_till)) : '-'),
    size: 203,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Created by',
    header: () => <FormattedMessage {...messages.created_by} />,
    accessorKey: 'created_by_details',
    enableSorting: false,
    cell: ({ row }) => {
      const email = row.original.created_by_details?.email
        ? row.original.created_by_details.email
        : row.original.created_by_details?.name
          ? row.original.created_by_details.name
          : '';
      return (
        <User
          data={{
            name: email,
          }}
          size="sm"
          hideImage={false}
          additionalContent={row.original.created_at ? formatDate(new Date(row.original.created_at), 'withTime') : ''}
        />
      );
    },
    size: 250,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
  },
  {
    id: 'menu',
    enableSorting: false,
    accessorKey: 'menu',
    size: 48,
    className: 'text-left right-0 sticky shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    cellClassName: 'text-center right-0 sticky z-[1] shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    header: ({ table }) => (
      <div>
        <Popover className="flex justify-end items-center pr-3">
          <PopoverButton className={classNames('focus:outline-none')}>
            <PlusCircleIcon
              className={classNames('text-black h-5 w-5 focus:outline-none')}
              aria-hidden="true"
              data-testid="hide-column"
            />
          </PopoverButton>
          <Transition>
            <PopoverPanel>
              <div
                className="bg-white border-solid border-[1px] w-[214px] font-normal shadow-sm max-h-[480px] overflow-y-auto rounded-md px-4 absolute right-0 top-12"
                data-testid="column-model"
              >
                {table.getAllLeafColumns().map((column) => {
                  if (column.id === 'menu') {
                    return null;
                  }
                  return (
                    <div key={column.id} className="h-[40px] w-full flex items-center">
                      <span className="pr-2 w-6 h-6">
                        <img src={gripHorizontalIcon} alt={EDIT_ALT} className="w-full h-full" />
                      </span>
                      <Checkbox
                        label={
                          typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : convertToTitleCase(column.id)
                        }
                        id={column.id}
                        size="md"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                        disabled={false}
                      />
                    </div>
                  );
                })}
              </div>
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
    ),
  },
];
